import { css } from '@linaria/core';

// SCSS variables converted to JS constants
export const THEME_HUE = 175;
export const ACCENT_HUE = 240;
export const CODE_BG = '#5f5f5f52';
export const MOBILE_MEDIA_QUERY = 'screen and (max-width: 901px)';
export const MOBILE_MEDIA_QUERY_HEIGHT_ADDON = '(max-height: 1000px)';
export const MOBILE_MEDIA_QUERY_WITH_HEIGHT = `${MOBILE_MEDIA_QUERY} and ${MOBILE_MEDIA_QUERY_HEIGHT_ADDON}`;

export const globals = css`
  :global() {
    html, body {
        overflow: hidden;
        overflow-y: auto;
        background: #0a0a0a;
        color: #e2e8f0;

        color-scheme: dark;

        width: 100vw;
        height: 100vh;
        margin: 0;

        line-height: 1.3;
        letter-spacing: 0.007em;
        word-spacing: 0.02em;

        scroll-behavior: smooth;

        position: absolute;
        top: 0;
        left: 0;

        font-size: 18px;
        text-wrap: pretty;

        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    }

    body > div:not([hidden]) {
        width: 100%;
        display: flex;
        flex-direction: column;
    }

    * {
        max-width: 100%;
    }

    @media (prefers-reduced-motion: reduce) {
        html, body {
            scroll-behavior: auto;
        }
    }

    h1 {
        color: #fff;
    }

    h2 {
        color: #eee;
    }

    h3 {
        color: #ddd;
    }

    p {
        margin: .6em 0;
    }

    main, article, footer {
        > :first-child:is(h1, h2, h3, h4, h5, h6) {
            margin-top: 0;
        }
    }

    main {
        margin-top: 0;

        // this set of rules allows the game image to go behind the rest of the content
        position: relative;
        z-index: 0;
    }

    .bprogress .spinner {
        --bprogress-spinner-right: 3em;
        --bprogress-spinner-bottom: 2em;
        --bprogress-spinner-border-size: 3px;
    }

    .bprogress .spinner-icon {
        scale: 3;
    }

    #progress-bar-container {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 999999999999;
        pointer-events: none;
    }

    :root {
        --link-txt-color-noncurrent-normal: hsl(${THEME_HUE}, 100%, 68%);
        --link-udl-color-noncurrent-normal: hsla(${THEME_HUE}, 100%, 40%, 0%);

        --link-txt-color-noncurrent-hover: hsl(${THEME_HUE}, 100%, 80%);
        --link-udl-color-noncurrent-hover: hsla(${THEME_HUE}, 100%, 60%, 90%);

        --link-txt-color-current-normal: hsl(${THEME_HUE}, 100%, 85%);
        --link-udl-color-current-normal: hsla(${THEME_HUE}, 100%, 70%, 0%);

        --link-txt-color-current-hover: hsl(${THEME_HUE}, 100%, 95%);
        --link-udl-color-current-hover: hsla(${THEME_HUE}, 100%, 70%, 90%);
    }

    a[data-no-link-style] {
        color: inherit;
        -webkit-text-decoration: inherit;
        text-decoration: inherit;
    }

    a:not([data-no-link-style]) {
        &, * {
            transition-property: color, text-decoration-color;
            transition-duration: 0.25s;
            transition-timing-function: cubic-bezier(0.19, 0.01, 0.23, 0.73);

            text-decoration: underline;
            text-decoration-thickness: 0.1em;

            color: var(--link-txt-color-noncurrent-normal);
            text-decoration-color: var(--link-udl-color-noncurrent-normal);
        }
        &:hover {
            &, * {
                transition-duration: 0.4s;
                transition-timing-function: cubic-bezier(0.13, 0.7, 0.52, 1);

                color: var(--link-txt-color-noncurrent-hover);
                text-decoration-color: var(--link-udl-color-noncurrent-hover);
            }
        }
        &[aria-current="page"] {
            &, * {
                color: var(--link-txt-color-current-normal);
                text-decoration-color: var(--link-udl-color-current-normal);
            }
            &:hover {
                &, * {
                    color: var(--link-txt-color-current-hover);
                    text-decoration-color: var(--link-udl-color-current-hover);
                }
            }
        }
    }

    pre, code {
        color: #ddd;
        font-family: var(--code-font);
        font-weight: 600;
        font-size: 0.9em;
        background: ${CODE_BG};
    }

    code {
        padding: 0.3em 0.5em 0.2em;
        border-radius: 0.3em;
        position: relative;
        line-height: 1.1;
        code {
            background: none;
        }
    }

    pre {
        border-radius: 8px;
        padding: 1em;
        margin: 1em 0;
        overflow-x: auto;
        max-height: 75vh;
        overflow-y: auto;
        line-height: 1.45;
        text-align: left;
        code {
            background: none;
        }
        > code {
            padding: 0;
            line-height: unset;
        }
        counter-reset: linenumber;
        backdrop-filter: blur(32px);
    }

    :root {
        --code-font: 'Source Code Pro', Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
        --tooltipBG: #0a241a;
    }

    main > section, footer {
        padding: min(2vw, 32px) min(3vw, 48px);
        width: 100%;
        box-sizing: border-box;
    }

    article, footer {
        overflow-wrap: break-word;
        word-wrap: break-word;
    }

    article, footer > :nth-child(2) {
        max-width: min(55em, calc(90vw - min(3vw, 48px)))
    }

    div:has(> footer) {
        // ensure footer is always at the bottom of the page, even if the content
        // is very short, while still keeping it below the fold if the content is too long
        position: sticky;
        top: 100%;
        background-color: #000;
    }

    footer {
        text-align: center;
        align-self: center;
        display: flex;
        align-items: center;
        justify-content: space-evenly;
        flex-wrap: wrap;
        text-wrap: balance;

        gap: 1rem 3rem;
        padding-bottom: 1.5rem;
        padding-top: 1.5rem;

        @media screen and (min-width: 1200px) {
            text-wrap: pretty;
        }

        > :nth-child(2) {
            min-width: 10em;
            max-width: 50em;
        }
        > :not(:nth-child(2)) {
            min-width: min-content;
            flex-grow: 1;
            flex-basis: min(4em, 100vw);
        }

        span {
            display: inline-block;
        }

        > :nth-child(3) > div {
            --link-txt-color-noncurrent-normal: hsl(${THEME_HUE}, 0%, 100%);
            --link-udl-color-noncurrent-normal: hsla(${THEME_HUE}, 100%, 40%, 0%);

            --link-txt-color-noncurrent-hover: hsl(${THEME_HUE}, 100%, 60%);
            --link-udl-color-noncurrent-hover: hsla(${THEME_HUE}, 100%, 50%, 90%);

            display: flex;
            gap: 1.75rem;
            justify-content: center;
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
            > a {
                display: flex;
                align-items: center;
                gap: 0.5em;
                justify-content: center;
                flex-wrap: wrap;

                > svg {
                    font-size: 1.4em;
                }
            }
        }

        //img {
        //    min-width: min-content;
        //    min-width: attr(width px);
        //    min-height: min-content;
        //    min-height: attr(height px);
        //}
    }

    hr {
        height: 0;
        width: 85%;
        justify-self: center;
    }
  }
`;
